<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
            <li class="pr-3"><a class="cursor-pointer">Automations</a></li>
            <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
            <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Reminders</a></li>
        </ul>

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Manage Reminders</h3>

                    <DataTable
                        :value="reminders"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['scheduled', 'clientName', 'appointmentId']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Scheduled Date, Client Name or Appointment #"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No reminders found.
                        </template>

                        <template #loading>
                            Loading reminders. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <div v-if="data.status != 'C'">
                                    <SplitButton
                                        icon="pi pi-pencil"
                                        label="Select"
                                        :model="getFilteredItems(data)"
                                        severity="success"
                                        @click="chooseBlankSelection(data)"
                                        class="mb-2 mr-2 ml-2 mt-2"
                                    />
                                </div>
                            </template>
                        </Column>

                        <Column field="scheduled" header="Scheduled" :sortable="true"/>

                        <Column field="type" header="Type" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Type</span>
                                <span v-if="data.type === 'E'"
                                      :class="'type-badge status-' + data.type">Email</span>
                                <span v-if="data.type === 'S'"
                                      :class="'type-badge status-' + data.type">SMS</span>
                                <span v-if="data.type === 'W'"
                                      :class="'type-badge status-' + data.type">WhatsApp</span>
                                <span v-if="data.type === 'P'"
                                      :class="'type-badge status-' + data.type">Phone Call</span>
                                <span v-if="data.type === 'I'"
                                      :class="'type-badge status-' + data.type">IsoEvolve Notification</span>
                            </template>
                        </Column>

                        <Column field="status" header="Status" :sortable="true">
                            <template #body="{data}">
                                <span class="p-column-title">Status</span>
                                <span v-if="data.status === 'C'"
                                      :class="'status-badge status-' + data.status">Completed</span>
                                <span v-if="data.status === 'P'"
                                      :class="'status-badge status-' + data.status">Pending</span>
                                <span v-if="data.status === 'E'"
                                      :class="'status-badge status-' + data.status">Error</span>
                            </template>
                        </Column>

                        <Column v-for="col in selectedColumns"
                                :key="col.field"
                                :field="col.field"
                                :header="col.header"
                                :sortable="col.sortable"/>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Replay Reminder for {{ this.reminder.clientName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Send this Reminder Again?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Replay" class="mr-2 mb-2" @click="replayReminder"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Complete Reminder for {{ this.reminder.clientName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Complete this Reminder?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Complete" class="mr-2 mb-2" @click="completeReminder"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import MessageSchedulerService from "@/service/MessageSchedulerService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }

    },
    created() {
        this.initFilters();

        this.columnsDefault = [
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'appointmentDate', header: 'Appointment Date', sortable: true}
        ];

        this.columnsFullList = [
            {field: 'clientName', header: 'Client Name', sortable: true},
            {field: 'appointmentId', header: 'Appointment #', sortable: true},
            {field: 'appointmentDate', header: 'Appointment Date', sortable: true},
        ];

        this.selectedColumns = this.columnsDefault;

        this.standardFilters = [
            {field: 'completed', header: 'Completed'},
            {field: 'pending', header: 'Pending'},
            {field: 'errors', header: 'Errors'}
        ];
    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            reminder: {},
            reminders: null,
            originalReminders: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ]
        }
    },
    watch: {
        reminders() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[AppointmentsNew][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [remindersData, companyConfigData] = await Promise.all([
                    MessageSchedulerService.getCompanyMessageSchedulings(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                ]);

                this.reminders = remindersData.data;
                this.originalReminders = remindersData.data;

                this.companyConfiguration = companyConfigData.data;
                console.log("[AppointmentsNew][mounted] companyConfigData.data = ", companyConfigData.data);

                this.loading = false;

            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching additional data = ", e);
            }

            console.log("[AppointmentsNew][mounted] this.reminders = ", this.reminders);
            console.log("[AppointmentsNew][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'scheduled': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'clientName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'appointmentId': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callReplayReminder(data) {
            this.reminder = data;
            this.confirmationResponse = 'N';
            this.activeTab = 2;
        },
        callCompleteReminder(data) {
            this.reminder = data;
            this.confirmationResponse = 'N';
            this.activeTab = 3;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isComplete = value.some(item => item.field === "complete");
            const isPending = value.some(item => item.field === "pending");
            const isErrors = value.some(item => item.field === "errors");

            if (this.reminders !== null) {

                if (isComplete) {
                    this.reminders = this.originalReminders.filter(reminder => reminder.status === 'C');
                } else if (isPending) {
                    this.reminders = this.originalReminders.filter(reminder => reminder.status === 'P');
                } else if (isErrors) {
                    this.reminders = this.originalReminders.filter(reminder => reminder.status === 'E');
                } else {
                    this.reminders = this.originalReminders;
                }

            } else {
                this.reminders = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            if (data.status != 'C') {
                items.push({
                    label: 'Replay Reminder',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callReplayReminder(data);
                    }
                });

                items.push({
                    label: 'Complete Reminder',
                    class: 'mb-4 mr-4 ml-4 mt-4',
                    command: () => {
                        this.callCompleteReminder(data);
                    }
                });
            }

            console.log('[getFilteredItems] items = ', items);

            // Store the computed items in the cache
            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async replayReminder() {
            console.log("[" + name + "][replayReminder] Start.");

            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.reminder.id) {

                    try {
                        const response = await MessageSchedulerService.processMessageScheduling(this.reminder.id);
                        console.log('[Appointments][deleteAppointment] Delete response = ', response);

                        const [remindersData] = await Promise.all([
                            MessageSchedulerService.getCompanyMessageSchedulings(this.profile.companyId)
                        ]);

                        this.reminders = remindersData.data;
                        this.originalReminders = remindersData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Replayed reminder',
                            life: 3000
                        });
                    } catch (error) {
                        console.error('[Appointments][deleteAppointment] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to replay reminder. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async completeReminder() {
            console.log("[" + name + "][completeReminder] Start.");

            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.reminder.id) {

                    try {
                        const response = await MessageSchedulerService.completeMessageScheduling(this.reminder.id);
                        console.log('[Appointments][deleteAppointment] Delete response = ', response);

                        const [remindersData] = await Promise.all([
                            MessageSchedulerService.getCompanyMessageSchedulings(this.profile.companyId)
                        ]);

                        this.reminders = remindersData.data;
                        this.originalReminders = remindersData.data;

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Completed reminder',
                            life: 3000
                        });
                    } catch (error) {
                        console.error('[Appointments][deleteAppointment] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to complete reminder. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    }
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-E {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-S {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-C {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-M {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-P {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-U {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-B {
        background: #b9a1de;
        color: #830293;
    }

}

.type-badge {
    &.status-P {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-B {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-E {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-S {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-W {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-I {
        background: #b9a1de;
        color: #830293;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }

}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
